import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaTimes } from 'react-icons/fa';

/**
 * FormSelect multiple option component with image/URL support
 * Dropdown multi-select that can display images or URLs for options
 */
const FormSelectMultiple = ({
  label = '',
  value = [],
  onChange = () => {},
  options = [],
  placeholder = 'Select options...',
  error = '',
  required = false,
  disabled = false,
  maxSelections = null,
  showImages = false,
  imageKey = 'image',
  labelKey = 'label',
  valueKey = 'value',
  className = '',
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      if (!maxSelections || value.length < maxSelections) {
        onChange([...value, optionValue]);
      }
    }
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const getOptionLabel = (optionValue) => {
    const option = options.find((opt) => opt[valueKey] === optionValue);
    return option ? option[labelKey] : optionValue;
  };

  const getOptionImage = (optionValue) => {
    const option = options.find((opt) => opt[valueKey] === optionValue);
    return option ? option[imageKey] : null;
  };

  return (
    <div className={className}>
      {/* Label */}
      {label && (
        <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-900 tw-mb-1">
          {label}
          {required && <span className="tw-text-red-500 tw-ml-1">*</span>}
        </label>
      )}

      {/* Main container */}
      <div className="tw-relative">
        {/* Selected values display */}
        <div
          onClick={() => !disabled && setOpen(!open)}
          className={`
            tw-min-h-10 tw-p-2 tw-border tw-border-slate-300 tw-rounded-md
            tw-bg-white tw-cursor-pointer
            tw-flex tw-items-center tw-gap-2 tw-flex-wrap
            ${disabled ? 'tw-bg-slate-100 tw-opacity-60 tw-cursor-not-allowed' : ''}
            ${error ? 'tw-border-red-500' : ''}
          `}
        >
          {value.length === 0 ? (
            <span className="tw-text-slate-500 tw-text-sm">{placeholder}</span>
          ) : (
            value.map((optionValue) => {
              const label = getOptionLabel(optionValue);
              const image = showImages ? getOptionImage(optionValue) : null;

              return (
                <div
                  key={optionValue}
                  className="tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-1 tw-rounded-sm tw-flex tw-items-center tw-gap-1 tw-text-sm"
                >
                  {image && (
                    <img
                      src={image}
                      alt={label}
                      className="tw-w-4 tw-h-4 tw-rounded-xl tw-object-cover"
                    />
                  )}
                  <span>{label}</span>
                  <button
                    type="button"
                    onClick={(e) => handleRemove(optionValue, e)}
                    className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-blue-600 hover:tw-text-blue-800"
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              );
            })
          )}

          {/* Chevron icon */}
          <div className="tw-ml-auto tw-flex-shrink-0">
            <FaChevronDown
              size={14}
              className="tw-text-slate-400"
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
              }}
            />
          </div>
        </div>

        {/* Dropdown menu */}
        {open && (
          <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white tw-border tw-border-slate-300 tw-rounded-md tw-shadow-lg tw-z-10">
            <div className="tw-max-h-64 tw-overflow-y-auto">
              {options.length === 0 ? (
                <div className="tw-p-2 tw-text-sm tw-text-slate-500">No options available</div>
              ) : (
                options.map((option) => {
                  const isSelected = value.includes(option[valueKey]);
                  const optionImage = showImages ? option[imageKey] : null;

                  return (
                    <button
                      key={option[valueKey]}
                      type="button"
                      onClick={() => handleToggle(option[valueKey])}
                      className={`
                        tw-w-full tw-text-left tw-px-3 tw-py-2
                        tw-flex tw-items-center tw-gap-2
                        tw-border-0 tw-bg-transparent tw-cursor-pointer
                        hover:tw-bg-slate-100
                        ${isSelected ? 'tw-bg-blue-50' : ''}
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="tw-cursor-pointer"
                      />
                      {optionImage && (
                        <img
                          src={optionImage}
                          alt={option[labelKey]}
                          className="tw-w-5 tw-h-5 tw-rounded-xl tw-object-cover"
                        />
                      )}
                      <span className="tw-text-sm tw-flex-1">{option[labelKey]}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && <div className="tw-text-xs tw-text-red-600 tw-mt-1">{error}</div>}

      {/* Helper text */}
      {maxSelections && (
        <div className="tw-text-xs tw-text-slate-500 tw-mt-1">
          {value.length} / {maxSelections} selected
        </div>
      )}
    </div>
  );
};

FormSelectMultiple.propTypes = {
  label: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      image: PropTypes.string,
    })
  ),
  placeholder: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  maxSelections: PropTypes.number,
  showImages: PropTypes.bool,
  imageKey: PropTypes.string,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  className: PropTypes.string,
};

export default FormSelectMultiple;
